import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  POS_PAYMENT_METHODS_DEFAULT,
  makePosPaymentMethod,
  type PosPaymentMethod,
} from '@mlm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getRoleOptions, getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { hasPerm } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';

/**
 * POS (Cashier) payment methods — admin-managed registry for the in-person
 * tender picker. Each row pairs a `tenderKind` (cash-with-change | exact | wallet)
 * with a wallet `currency` (for wallet methods) and an optional per-role
 * restriction (`roleCodes` — empty means every cashier). Saving needs the
 * grantable `tenant.payment_methods.write` (super_admin passes implicitly).
 * Mirrors the typed-editor pattern in `ecom-payment-methods.tsx` /
 * `payout-deductions.tsx`.
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

type RoleOption = { code: string; name: string };

export function PosPaymentMethodsPage() {
  const qc = useQueryClient();
  const canWrite = hasPerm('tenant.payment_methods.write');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  // Role choices for the per-method "Allowed roles" picker. On error the picker
  // degrades to a read-only code list rather than blocking the whole editor.
  const rolesQ = useQuery({ queryKey: ['role-options'], queryFn: getRoleOptions });

  const [methods, setMethods] = useState<PosPaymentMethod[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const cfg = q.data.pos_payment_methods ?? POS_PAYMENT_METHODS_DEFAULT;
    setMethods(Array.isArray(cfg.methods) ? clone(cfg.methods) : []);
  }, [q.data]);

  // `roleCodes: []` and an absent field mean the same thing ("all cashiers") —
  // persist the canonical absent form so the stored config never carries an
  // empty list.
  const normalizedMethods = methods.map(({ roleCodes, ...m }) =>
    roleCodes && roleCodes.length > 0 ? { ...m, roleCodes } : m,
  );

  const save = useMutation({
    meta: { successMessage: 'POS payment methods saved.' },
    mutationFn: () => patchTenantFeatures({ pos_payment_methods: { methods: normalizedMethods } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateMethod(i: number, patch: Partial<PosPaymentMethod>) {
    setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function addMethod() {
    setMethods((ms) => [...ms, makePosPaymentMethod()]);
  }
  function removeMethod(i: number) {
    setMethods((ms) => ms.filter((_, idx) => idx !== i));
  }

  function validate(): string | null {
    const codes = methods.map((m) => m.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every method needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Method codes must be unique.';
    else if (methods.some((m) => m.label.trim() === '')) msg = 'Every method needs a label.';
    // Cash/exact tenders are always cash-denominated; only a wallet tender may
    // debit a non-cash (GC/Coin) balance. A wallet method may debit the CASH
    // wallet too — the buyer pays the normal cash total from their cash balance.
    else if (methods.some((m) => m.tenderKind !== 'wallet' && m.currency !== 'cash'))
      msg = 'Only wallet methods can use a GC/Coin currency — set cash/exact methods to Cash.';
    else if (!methods.some((m) => m.enabled))
      msg = 'Enable at least one method — the cashier needs a way to take payment.';
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">POS payment methods</h1>
        <p className="text-sm text-muted-foreground">
          The tender options the cashier picks at checkout. <b>Cash</b> takes an amount tendered and
          gives change; <b>Exact</b> requires the tendered amount to equal the total (e.g. a card
          swipe, no change); <b>Wallet</b> debits the buyer slot's balance in the chosen currency —
          Cash (pays the normal cash total from the member's cash wallet), GC, or Coin. Each sale
          records the method for the daily-sales breakdown.
        </p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground mt-1">
            Read-only. Saving needs the &ldquo;Edit payment methods &amp; POS fees&rdquo;
            permission (<span className="font-mono">tenant.payment_methods.write</span>).
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {methods.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No payment methods yet.{' '}
                {canWrite
                  ? 'Add one below.'
                  : 'An admin with the payment-methods edit permission can add them.'}{' '}
                The cashier can't take payment until at least one method is enabled.
              </CardContent>
            </Card>
          )}

          {methods.map((m, i) => (
            <MethodCard
              key={i}
              method={m}
              canWrite={canWrite}
              roleOptions={rolesQ.data}
              roleOptionsFailed={rolesQ.isError}
              onChange={(patch) => updateMethod(i, patch)}
              onRemove={() => removeMethod(i)}
            />
          ))}

          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addMethod}>
                <Plus className="h-4 w-4" /> Add method
              </Button>
              <ConfirmSaveButton
                size="sm"
                label="Save"
                title="Save POS payment methods"
                current={q.data?.pos_payment_methods}
                next={{ methods: normalizedMethods }}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {save.isSuccess && !save.isPending && (
                <span className="text-xs text-success">Saved.</span>
              )}
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.error && (
                <span className="text-xs text-destructive">Save failed. Try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MethodCard({
  method: m,
  canWrite,
  roleOptions,
  roleOptionsFailed,
  onChange,
  onRemove,
}: {
  method: PosPaymentMethod;
  canWrite: boolean;
  /** undefined while loading; the picker renders once options arrive. */
  roleOptions?: RoleOption[];
  /** Options fetch failed — degrade to a read-only code list. */
  roleOptionsFailed: boolean;
  onChange: (patch: Partial<PosPaymentMethod>) => void;
  onRemove: () => void;
}) {
  const isWallet = m.tenderKind === 'wallet';
  const selectedRoles = m.roleCodes ?? [];
  // Saved codes whose role has since been deleted/renamed — surfaced as a
  // warning chip (they'd silently keep restricting the tender otherwise).
  const orphanRoles = roleOptions
    ? selectedRoles.filter((c) => !roleOptions.some((r) => r.code === c))
    : [];
  const setRoles = (next: string[]) => onChange({ roleCodes: next });
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={m.code}
              disabled={!canWrite}
              placeholder="gcash"
              onChange={(e) => onChange({ code: e.target.value.trim() })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Label</label>
            <Input
              value={m.label}
              disabled={!canWrite}
              placeholder="GCash"
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tender</label>
            <Select
              value={m.tenderKind}
              disabled={!canWrite}
              onValueChange={(v) => {
                const tenderKind = v as PosPaymentMethod['tenderKind'];
                // A cash/exact tender is always cash. A wallet tender keeps its
                // currency (cash / gc / coin) — cash-wallet debits the cash balance.
                onChange({
                  tenderKind,
                  currency: tenderKind === 'wallet' ? m.currency : 'cash',
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash (with change)</SelectItem>
                <SelectItem value="exact">Exact (no change)</SelectItem>
                <SelectItem value="wallet">Wallet debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Currency</label>
            <Select
              value={m.currency}
              disabled={!canWrite || !isWallet}
              onValueChange={(v) => onChange({ currency: v as PosPaymentMethod['currency'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gc">GC</SelectItem>
                <SelectItem value="coin">Coin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Allowed roles</label>
          {roleOptionsFailed ? (
            // Options unavailable — show what's stored, read-only, rather than
            // hiding a live restriction or letting a blind edit clobber it.
            <p className="text-xs text-muted-foreground">
              Couldn't load the role list.{' '}
              {selectedRoles.length > 0 ? (
                <>
                  Restricted to: <span className="font-mono">{selectedRoles.join(', ')}</span>
                </>
              ) : (
                'Open to all cashiers.'
              )}
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {!roleOptions && (
                <span className="text-xs text-muted-foreground">Loading roles…</span>
              )}
              {(roleOptions ?? []).map((r) => {
                const on = selectedRoles.includes(r.code);
                return (
                  <label
                    key={r.code}
                    className="flex items-center gap-1.5 rounded-full border border-input px-2.5 py-1 text-xs"
                  >
                    <Checkbox
                      checked={on}
                      disabled={!canWrite}
                      onCheckedChange={(v) =>
                        setRoles(
                          v === true
                            ? [...selectedRoles, r.code]
                            : selectedRoles.filter((c) => c !== r.code),
                        )
                      }
                    />
                    {r.name}
                  </label>
                );
              })}
              {orphanRoles.map((code) => (
                <Badge key={code} variant="warning" className="gap-1 normal-case">
                  <span className="font-mono">{code}</span> (deleted role)
                  {canWrite && (
                    <button
                      type="button"
                      aria-label={`Remove ${code}`}
                      className="ml-0.5 leading-none"
                      onClick={() => setRoles(selectedRoles.filter((c) => c !== code))}
                    >
                      ×
                    </button>
                  )}
                </Badge>
              ))}
              {roleOptions && selectedRoles.length === 0 && (
                <span className="text-xs text-muted-foreground">All cashiers</span>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Tick roles to restrict who can take this tender; leave every box unticked to allow all
            cashiers. A restricted method is hidden from other tills and rejected at checkout.
          </p>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={m.enabled}
              disabled={!canWrite}
              onCheckedChange={(v) => onChange({ enabled: v === true })}
            />
            Enabled
          </label>
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
